import React from 'react';
import { Platform } from 'react-native';
import {
    CloudStorage,
    CloudStorageScope
} from 'react-native-cloud-storage';
import fs from 'react-native-fs';

import actions from '../../../../actions';
import { Page } from '../../../../common';
import { FileUploadService } from '../../../../services/FileUploadService';
import store from '../../../../store';
import { formatDateShort } from '../../../../utils';
import { extractFileNameFromFullPath } from '../../../../utils/files';
import { delay } from '../../../../utils/fn';
import { consoleLog } from '../../../../utils/log';
import { getDeviceId } from '../Backup';
import { BackupList } from '../BackupList';
import {
    CLEAR_DELETED_ATTACHES_AFTER_BACKUP,
    clearDeletedAttaches,
    getCurrentState,
    getDeletedAttaches,
    getFullBackupData,
    getProfileInfo,
    restoreData,
    setLastBackupState
} from '../common/backupData';
import {
    getDevicesData,
    setDevicesData
} from './iCloudDevices';
import {
    compareFileName,
    decodeAttach,
    getFileFromCloud,
    getFileList,
    getICloudFileListWithSizes,
    getICloudFileName,
    ICLOUD_SCOPE,
    isAttachFile,
    isBackupFile,
    isProfileFile,
    waitForFileSync
} from './iCloudFiles';
import {
    getMetadata,
    setMetadata
} from './iCloudMetadata';
import { startICloudRestoreAttaches } from './iCloudRestoreAttaches';


export const icloudFileListShort = (list, withDate = false) => list?.map?.(f => typeof f === 'object' ? `${f?.file ?? 'n/a'} (${f?.size ?? 0})${withDate && f?.birthtimeMs ? `{${formatDateShort(f?.birthtimeMs)}}` : ''}` : f);

const toLog = (name, ...params) => {
    if (Object.iOSLog === false) {
        //return;
    }
    actions.addLog(`${name}`, params);
    const isError = name?.indexOf('error') >= 0 || name?.indexOf('Error') >= 0;
    if (isError) {
        console.error(`%c${name}`, 'color: #880000', ...params);

    } else {
        console.log(`%c${name}`, 'color: #009900', ...params);
    }
};

export const fetchMetadataFiles = async (profileId) => {
    if (Platform.OS !== 'ios') {
        return;
    }
    toLog('Fetch metadata files', profileId);
    await CloudStorage.downloadFile('/devices.json', ICLOUD_SCOPE);
    await CloudStorage.downloadFile(getICloudFileName({
        profileId,
        type: 'metadata',
        deviceId: '',
        name: '',
    }), CloudStorageScope.AppData);
    const files = await getFileList();
    const metadataFiles = files?.filter((f) => f?.endsWith?.('.metadata.icloud') || f?.endsWith('.metadata')) || [];
    toLog('ICloud fetchMetadataFiles', {
        files: icloudFileListShort(files),
        metadataFiles: icloudFileListShort(metadataFiles),
    });
    return metadataFiles;
};

/**
 * Fetch all data files from iCloud.
 * This function retrieves the list of files from iCloud,
 * filters them to find files that end with '.json.icloud',
 * and triggers a sync for each data file found.
 * It returns an array of data files that match the specified criteria.
 * If the platform is not iOS, it returns an empty array.
 * The function also logs the files and data files found in iCloud.
 * @returns {Promise<*[]>}
 */
export const fetchAllDataFiles = async () => {
    if (Platform.OS !== 'ios') {
        return [];
    }
    try {
        await setDevicesData();
        toLog('fetchAllDataFiles called');
        const files = await getFileList();
        const dataFiles = files?.filter((f) => f?.endsWith?.('.json.icloud') || f?.endsWith('.json')) || [];

        for (const file of dataFiles) {
            toLog('trigger sync for file', file);
            try {
                await CloudStorage.downloadFile(file, ICLOUD_SCOPE);
            } catch (e) {
                toLog('error', 'trigger sync', e);
            }
            toLog('trigger sync for file done', file);
        }
        const files1 = await getFileList();
        toLog('ICloud fetchAllDataFiles:triggerSync', {
            files: icloudFileListShort(files),
            files1: icloudFileListShort(files1),
            dataFiles: icloudFileListShort(dataFiles),
        });
        return dataFiles;
    } catch (e) {
        toLog('fetchAllDataFiles error (iCloud unavailable)', e?.message ?? e);
        return [];
    }
};


/**
 * Get the last backups for a specific profile.
 * This function retrieves the metadata for the specified profile,
 * checks if the backup files exist for each device,
 * and returns an array of backup objects containing device ID, file name, device information,
 * and file information.
 * If no backups are found, it returns an empty array.
 * If the profile ID is not provided, it throws an error.
 * @param profileId
 * @returns {Promise<*[]|null>}
 */
export const getLastBackups = async ({ profileId }) => {
    if (!profileId) {
        throw new Error(Object.R('errors.noProfileIdForBackup'));
    }
    const metadata = await getMetadata({ profileId });
    if (!metadata?.data) {
        toLog('ICloud:getLastBackup: no metadata found');
        return null;
    }
    const devices = await getDevicesData();
    toLog('ICloud:getLastBackup:metadata', {
        devices,
        metadata,
    });
    const backups = [];
    const files = await getICloudFileListWithSizes();

    const attaches = files?.filter?.(({ file }) => isAttachFile(file, profileId, false)) || [];
    toLog('getLastBackups:attaches', {
        attaches: icloudFileListShort(attaches),
    });
    const sizes = attaches?.reduce?.((acc, f) => acc + (f.size || 0), 0);

    for (const deviceId of Object.keys(metadata.data)) {
        const fileName = metadata.data[deviceId];
        if (fileName && isBackupFile(fileName, profileId)) {
            const file = files?.find?.((f) => compareFileName({
                fileName1: f.file,
                fileName2: fileName,
                ignoreDevice: true,
            }) || f.file === fileName);
            if (!file) {
                toLog('ICloud:getLastBackups: file not found', {
                    fileName,
                    deviceId,
                    files,
                });
            }
            backups.push({
                deviceId,
                fileName,
                device: devices?.[deviceId] || {},
                file: file || {},
                size: sizes + file?.size || 0,
                createdTime: file ? new Date(file?.birthtime || 0) : null,
            });
        }
    }
    toLog('getLastBackups', {
        profileId,
        attaches,
        metadata,
        devices,
        files,
        backups,
    });
    toLog('getLastBackups:sizes', {
        sizes,
    });
    return backups?.sort((a, b) => (b?.file?.birthtimeMs || 0) - (a?.file?.birthtimeMs || 0)) || [];
};

/**
 * Get the last backup file name for a specific profile and device.
 * This function retrieves the metadata for the specified profile,
 * checks if the backup file exists for the current device,
 * and returns the backup file name if it is valid.
 * If the backup file does not exist or is not valid, it returns null.
 * @deprecated
 * @param profileId
 * @param deviceId
 * @returns {Promise<*|null>}
 */
export const getLastBackupFileName = async ({
    profileId,
    deviceId,
} = {}) => {
    const device = deviceId || await getDeviceId();
    try {
        const metadata = await getMetadata({ profileId });
        const backupFileName = metadata?.data?.[device];
        toLog('ICloud:getLastBackupFileName:metadata', {
            metadata,
            backupFileName,
            profileId,
            device,
        });
        return isBackupFile(backupFileName, profileId) ? backupFileName : null;
    } catch (e) {
        return null;
    }
};

/**
 * Finish iCloud backup process.
 * This function sets the metadata for the backup file, indicating that the backup has been completed.
 * It updates the metadata with the current profile ID and the backup file name.
 * @param backupFileName
 * @returns {Promise<boolean>}
 */
export const finishICloudBackup = async ({
    backupFileName,
}) => {
    toLog('ICloud:finish:start', {
        backupFileName,
    });
    const metadata = await setMetadata({
        profileId: getProfileInfo()?.profileId,
        fileName: backupFileName,
    });
    return !!metadata;
};

const uploadFunction = async ({
    inFile,
    outFile,
}, { profileId }) => {
    toLog('uploadFunction', {
        inFile,
        outFile,
        profileId,
    });
    const fileName = inFile?.split?.('/')
        .pop();
    const outFileName = getICloudFileName({
        profileId,
        type: 'attach',
        name: inFile,
        deviceId: (await getDeviceId()),
    });
    toLog('uploadFunction:params', {
        inFile,
        outFile,
        fileName,
        outFileName,
        profileId,
    });
    const data = await fs.readFile(inFile, 'base64');
    await CloudStorage.writeFile(outFileName, data, ICLOUD_SCOPE);
};

/**
 * Remove unused attachments from iCloud.
 * This function retrieves the list of files in iCloud,
 * filters them to find files associated with the specified profile ID,
 * and compares them with the provided attachments.
 * It identifies files that are not present in the attachments list and deletes them from iCloud.
 * It returns a promise that resolves with the results of the deletion operation.
 * @param attachments - array of attachment objects, each containing a path.
 * @param profileId
 * @returns {Promise<Awaited<unknown>[]>}
 */
const removeUnusedAttachments = async ({
    // attachments,
    profileId,
}) => {
    const files = await getICloudFileListWithSizes();
    const profileFiles = files?.filter?.(({ file }) => isAttachFile(file, profileId)) ?? [];
    const att = await getDeletedAttaches();
    const attachesForRemove = att?.map(name => ({
        name: extractFileNameFromFullPath(name),
        file: name,
    }));
    const toDelete = [];
    toLog('removeUnusedAttach start', {
        profileId,
        att,
        files,
        attachesForRemove,
    });
    if (attachesForRemove?.length) {
        try {

            const decodedFiles = (files?.files ?? files).map?.(f => ({
                ...f,
                decodedFile: decodeAttach(f?.file, profileId),
            }));
            attachesForRemove?.forEach?.(({
                name,
                file,
            }) => {
                const found = decodedFiles?.find?.(f => f?.file?.endsWith(name) || f?.file?.endsWith(`${name}.icloud`));
                if (found) {
                    toLog('for unlink ', {
                        found,
                        name,
                        file,
                    });
                    toDelete.push(found?.file ?? found?.name);
                }
            });
        } catch (e) {
            toLog('error', 'remove icloud attaches error');
        }
    }

    const toDeletePromise = toDelete?.filter?.(f => Boolean(f))
        ?.map
        ?.((f) => CloudStorage.unlink(f, ICLOUD_SCOPE)) ?? [];
    toLog('iCloud:removeUnusedAttachments:start unlink', {
        files: icloudFileListShort(files),
        profileId,
        profileFiles: icloudFileListShort(profileFiles),
        toDelete,
        toDeletePromise,
    });
    if (CLEAR_DELETED_ATTACHES_AFTER_BACKUP) {
        await clearDeletedAttaches();
    }
    return Promise.allSettled(toDeletePromise);
};

/**
 * Remove unused backups from iCloud.
 * This function retrieves the last backup file name for the specified profile,
 * lists all files in iCloud, filters them to find profile-specific backup files,
 * and deletes any backup files that are not the last backup file.
 * It returns a promise that resolves with the result of the deletion operation.
 * @deprecated
 * @param {profileId}
 * @returns {Promise<{length?: PromiseSettledResult<[]>}>}
 */
export const removeUnusedBackups = async ({ profileId }) => {
    // const deviceId = await getDeviceId();
    await fetchMetadataFiles(profileId);
    await delay(2000);
    const { data: metadata } = (await getMetadata({ profileId })) ?? {};
    const backupFileNames = Object.values(metadata) ?? [];
    const files = await getICloudFileListWithSizes();
    const profileFiles = files?.filter?.(({ file }) => isBackupFile(file, profileId, false)) ?? [];
    const backupFiles =
        profileFiles?.filter?.(({ file }) => {
            toLog('check need to remove', {
                file,
                backupFileNames,
            });
            return !backupFileNames?.includes?.(file);
        }) ?? [];
    const toDelete = [ ...backupFiles ];
    toLog('removeUnusedBackups unlink', {
        backupFiles,
        files,
        profileFiles,
        toDelete,
    });
    const toDeletePromise = toDelete?.map?.(({ file }) => CloudStorage.unlink(file, ICLOUD_SCOPE)) ?? [];
    return await Promise.allSettled(toDeletePromise);
};

/**
 * Backup iCloud attachments.
 * This function uploads attachments to iCloud if they are not already present in the specified file list.
 * It filters the attachments based on their path and name, checks if they already exist in the file list,
 * and uploads only those that are not found. The upload is performed using the FileUploadService.
 * If the upload is successful, it calls the onSuccess callback with the result.
 * If an error occurs during the upload, it calls the onError callback.
 * @param attachments - array of attachment objects, each containing a path and name.
 * @param list - array of files from cloud storage, used to check existing files.
 * @param profileId
 * @param onSuccess
 * @param onError
 * @param onComplete
 * @returns {Promise<*[]>}
 */
export const backupICloudAttachments = async ({
    attachments = [],
    list = [],
    profileId,
    onSuccess,
    onError,
    onComplete,
}) => {
    if (!attachments?.length) {
        onSuccess?.([]);
        return [];
    }
    const files = list?.filter?.(({ file }) => isAttachFile(file, profileId)) ?? [];
    toLog('UploadFiles:iCloud', {
        files: icloudFileListShort(files),
        list: icloudFileListShort(list),
        profileId,
        attachments: icloudFileListShort(attachments),
    });
    if (!files?.length) {
        onSuccess?.([]);
    }

    const toUpload = (
        attachments?.filter(({
            path,
            name,
        }) => {
            const f = getICloudFileName({
                profileId,
                type: 'attach',
                name: path,
            });
            const res = files?.find(({ file }) => f === file);
            toLog('UploadFiles:iCloud:check', {
                f,
                files: icloudFileListShort(files)
                    .join(', '),
                res,
                path,
                name,
            });
            return !res;
        }) ?? []
    ).map((attachment) => ({
        inFile: attachment.path,
    }));

    toLog('UploadFiles:iCloud:2upload', { toUpload });
    if (!toUpload?.length) {
        toLog('UploadFiles:iCloud - no files to upload');
        onSuccess?.([]);
        return [];
    }
    toLog('UploadFiles:iCloud - start', { toUpload });
    const fileUpload = new FileUploadService();
    const res = await fileUpload.uploadFiles({
        files: toUpload,
        uploadFunction,
        uploadParams: {
            profileId,
        },
        callbacks: {
            onComplete,
            onError: onError,
        },
        onSuccess: onSuccess,
    });
    toLog('UploadFiles:iCloud - finish', {
        toUpload: icloudFileListShort(toUpload)
            ?.join(', '),
        res,
    });
    // onSuccess?.();
};

/**
 * Start iCloud backup process.
 * This function checks if iCloud backup is enabled, verifies if a backup is already in progress,
 * and then proceeds to create a backup file with the current profile data.
 * It uploads attachments to iCloud and finishes the backup process by updating the metadata.
 * If any errors occur during the process, it calls the onError callback.
 * @param onSuccess
 * @param onError
 * @returns {Promise<void>}
 */
export const startICloudBackup = async ({
    onSuccess,
    onError,
} = {}) => {
    if (Platform.OS !== 'ios') {
        return;
    }
    const {
        profileId,
    } = getProfileInfo();
    const state = getCurrentState();

    try {
        const {
            iCloudEnabled = false,
            // backupAttachmentWifi = true,
            // backupVisitsAttachments = false,
            // backupMyDocs = false,
        } = state?.prefs?.data ?? {};
        const { backupProcessing = false } = state?.forms?.values ?? {};
        await setDevicesData();
        const deviceId = await getDeviceId();
        const dataFileName = getICloudFileName({
            profileId,
            type: 'backup',
            deviceId,
        });

        if (iCloudEnabled === false) {
            actions.setFormValue('iCloudBackupProcessMessages', Object.R('errors.iCloudBackupDisabled'));
            // toLog('ICloud:2', { message: 'iCloud backup is disabled' });
            return;
        }

        if (backupProcessing) {
            actions.setFormValue('iCloudBackupProcessMessages', Object.R('errors.iCloudBackupAlreadyStarted'));
            // toLog('ICloud:3', { message: 'iCloud backup is disabled' });
            return;
        }

        actions.setFormValue('backupProcessing', true);
        const {
            backupData,
            data,
        } = await getFullBackupData();
        consoleLog('Full backup data', data, backupData);
        if (!backupData?.encryptedData || !backupData?.profileId) {
            toLog('ICloud:4', { message: 'No data or profile for backup' });
            throw new Error('errors.noDataOrProfileForBackup');
        }
        await CloudStorage.writeFile(dataFileName, JSON.stringify(backupData), ICLOUD_SCOPE);
        toLog('ICloud:backupFileCreated', {
            dataFileName,
            backupData,
        });
        const list = await getICloudFileListWithSizes();
        await backupICloudAttachments({
            fileList: list,
            attachments: data?.attachments,
            profileId,
            list,
            onError: (err) => {
                onError?.(err);
                removeUnusedBackups({ profileId });
            },
            onSuccess: (params) => {
                setLastBackupState({
                    state: 'success',
                    error: '',
                });
                toLog('ICloud:backupICloudAttachments:success', params);

                finishICloudBackup({
                    backupFileName: dataFileName,
                })
                    .then((res) => {
                        toLog('ICloud:finish:success', {
                            dataFileName,
                            list: list?.map(f => `${f?.file || 'n/a'} (${f?.size ?? 0})`),
                            res,
                        });
                        onSuccess?.(res);
                        setLastBackupState({
                            state: 'success',
                            error: '',
                        });
                        // return getFileList();
                    })
                    .catch((e) => {
                        toLog('ICloud:finish:error', {
                            dataFileName,
                            list: list?.map(f => `${f?.file || 'n/a'} (${f?.size ?? 0})`),
                            error:
                            e,
                        });
                        onError?.(e);
                    })
                    .finally(() => {
                        toLog('ICloud:finish:finally', { message: 'Removing unused backups' });
                        removeUnusedBackups({ profileId });
                        removeUnusedAttachments({
                            attachments: data?.attachments,
                            profileId,
                        });

                    });
            },
        });
    } catch (e) {
        toLog('ICloud:error catch', {
            backup: e,
            message: 'Error writing file to iCloud',
        });
        onError?.(e);
        throw new Error(e);
    } finally {
        actions.setFormValue('backupProcessing', false);
    }
};

export const retrieveICloudAttaches = async (attachments = []) => {
    if (!attachments?.length) {
        return [];
    }
    toLog('ICloud:retrieveICloudAttaches', attachments);
    const syncPromises = attachments?.map?.((attachment) => waitForFileSync(attachment?.file, 10, 1000)) || [];
    await Promise.allSettled(syncPromises);
    const downloadPromises =
        attachments?.map?.(async (attachment) => {
            const path = attachment?.localPath ?? attachment?.path;
            const exists = await fs.exists(attachment?.localPath);
            if (exists) {
                return {
                    path,
                    restore: false,
                    exists: true,
                };
            }
            const content = await CloudStorage.readFile(attachment?.file, ICLOUD_SCOPE);
            await fs.writeFile(path, content, { encoding: 'base64' });
            return {
                path,
                restore: true,
            };
        }) ?? [];
    return (await Promise.allSettled(downloadPromises));
};

/**
 * Get iCloud backup information for a specific profile.
 * This function retrieves the list of files in iCloud, filters them based on the profile ID,
 * and calculates the total size of attachments. It also retrieves the last backups and devices data.
 * The result includes the list of files, total attachment size, last backups, profile ID,
 * backups, and devices data.
 * @param profileId
 * @returns {Promise<{files: {file: string, size: number, isFile: Function, isDirectory: Function, birthtimeMs: number, mtimeMs: number}[], attachSize: unknown, lastBackups: *[], profileId, backups: *[], devices: ({}|*)}>}
 */
export const getICloudBackupInfo = async (profileId) => {
    consoleLog('ICloud backup info start', profileId);
    const lastBackups = await getLastBackups({ profileId });
    consoleLog('ICloud backup info start : lastBackups', lastBackups);
    //await delay(10000);
    const list = await getFileList();
    consoleLog('ICloud backup info list', list);
    const filteredList = list?.filter?.(f => isProfileFile(f, profileId, false)) ?? [];
    const files = await getICloudFileListWithSizes(filteredList);
    const devices = await getDevicesData();
    const attachSize = 0;

    try {

        const backups = lastBackups?.map?.((backup) => ({
            ...backup,
            name: backup.fileName,
            createdTime: backup?.file?.birthtimeMs,
            attachSize: backup?.file?.size,
        })) || [];
        toLog('ICloud:iCloudBackupInfo', {
            files: icloudFileListShort(files),
            lastBackups,
            backupsShort: backups?.map(b => ({
                ...b,
                file: icloudFileListShort([ b?.file ]),
            })),
            backups,
            attachSize,
            profileId,
            devices,
        });
        const result = {
            files,
            attachSize,
            lastBackups,
            profileId,
            backups,
            devices,
        };
        consoleLog('ICloud backup info result', result);
        return result;
    } catch (e) {
        toLog('ICloud:getICloudBackupInfo:error', e);
        return {
            files,
            attachSize,
            lastBackups,
            profileId,
            backups: [],
            devices,
        };

    }
};

export const loadBackupInfo = async ({ backupInfo } = {}) => {
    const {
        deviceId,
        fileName,
    } = backupInfo ?? {};
    const {
        hashKey,
        profileId,
    } = getProfileInfo();
    const result = {
        attachments: [],
        content: '',
        hashKey,
        profileId,
    };
    if (!backupInfo || !deviceId || !fileName) {
        toLog('ICloud:loadBackupInfo: no backup info provided', backupInfo);
        return result;
    }
    const content = await getFileFromCloud({
        fileName,
        withData: true,
        tryToDownload: true,
        attempts: 20,
        timeout: 1000,
    });
    result.content = content?.data || '';
    toLog('ICloud:loadBackupInfo', {
        result,
        backupInfo,
    });
    return result;
};


export const renameBackupToDevice = async ({
    file,
    profileId,
    deviceId,
} = {}) => {
    consoleLog('Rename backup for device', {
        file,
        profileId,
        deviceId,
    });
    if (!file) {
        return null;
    }
    return setMetadata({
        profileId,
        fileName: file,
        deviceId,
    });
};
export const restoreICloudBackup = async ({
    backup,
    onError,
    onComplete,
}) => {
    if (!backup) {
        onError?.(Object.R('errors.noBackupForRestore'));
        throw new Error(Object.R('errors.noBackupForRestore'));
    }

    const state = store?.getState?.();
    const { backupProcessing } = state?.forms?.values ?? {};
    const profileId = state?.user?.info?.userId ?? '';
    const hashKey = state?.user?.info?.hashKey ?? '';

    // return;
    if (backupProcessing) {
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.iCloudRestoreAlreadyStarted'));
        onError?.(Object.R('errors.iCloudRestoreAlreadyStarted'));
        return;
    }
    actions.setFormValue('backupProcessing', true);
    await startICloudRestoreAttaches({ profileId });

    try {
        const backupInfo = await loadBackupInfo({ backupInfo: backup });
        const file = JSON.parse(backupInfo?.content ?? '{}');
        toLog('ICloud:restore:backupInfo', {
            backup,
            backupInfo: {
                ...backupInfo,
            },
            file,
            profileId,
            hashKey,
        });
        // return;
        const data = await restoreData({
            file,
            profileId,
            hashKey,
            restore: true,
        });
        const files = await getICloudFileListWithSizes();
        const log = [];
        const attachmentFiles = files?.filter?.((f) => {
            const res = isAttachFile(f.file, profileId);
            log.push({
                res,
                f,
                profileId,
            });
            return res;
        }) || [];
        const attachments = attachmentFiles?.map?.((f) => ({
            ...f,
            localPath: `file://${fs.DocumentDirectoryPath}/${decodeAttach(f?.file, profileId)}`,
        })) || [];
        let attachesResult = null;
        if (attachments?.length) {
            attachesResult = await retrieveICloudAttaches(attachments);
        }
        toLog('ICloud:restoreBackup:finish:rename', {
            backupInfo,
            data,
            attachesResult,
            files,
            attachmentFiles,
            attachments,
            log,
            backup,
        });
        await renameBackupToDevice({
            file: backup?.file?.file?.file ?? backup?.file?.file,
            profileId,
        });
        onComplete?.();
    } catch (e) {
        onError?.(e);
        toLog('Error getting iCloud backup info', e);
    } finally {
        actions.setFormValue('backupProcessing', false);
    }
};

export const restoreICloud = async ({
    info,
    onComplete,
    onError,
    onCancel,
    alwaysChoose = false,
    renameToDevice = true,
}) => {
    const { backups } = info || {};
    // console.log('ICloud backup info:', {
    //     backups,
    //     info,
    // });
    if (backups?.length === 1) {
        toLog('Restoring iCloud backup', {
            ...backups[0],
            file: backups?.[0]?.file?.file,
            fileSize: backups?.[0]?.file?.size,
            backups,
        });
        if (alwaysChoose) {
            Page.showModal(<BackupList
                list={backups}
                attachSize={info?.attachSize}
                devices={info?.devices ?? {}}
                isIos
                onCancel={() => {
                    onCancel?.(Object.R('titles.userCancelled'));
                }}
                onSelect={(i) => {
                    toLog('User selected iCloud backup to restore', i);
                    restoreICloudBackup({
                        renameToDevice,
                        backup: i,
                        onComplete,
                        onError,
                    });
                }}/>, () => {
                console.log('ICloud restore modal closed');
            });
        } else {
            restoreICloudBackup({
                backup: backups?.[0],
                onComplete,
                onError,
                renameToDevice,
            });
        }
    } else if (backups?.length > 1) {
        toLog('Multiple iCloud backups found, showing list', backups);
        Page.showModal(<BackupList
            list={backups}
            attachSize={info?.attachSize}
            devices={info?.devices ?? {}}
            isIos
            onCancel={onCancel?.(Object.R('titles.userCancelled'))}
            onSelect={(i) => {
                toLog('User selected iCloud backup to restore multi', i);
                restoreICloudBackup({
                    backup: i,
                    onComplete,
                    onError,
                });
            }}/>);
    }

};

export const removeBackup = async ({
    backup,
    profileId,
}) => {
    toLog('Remove backup', backup);
    if (!backup) {
        toLog('Empty backup, exit');
        return 'Empty backup';
    }
    if (typeof backup?.file === 'object' && !Object.keys(backup?.file).length) {
        const md = await getMetadata({ profileId });
        toLog('Metadata', md);
    }
};
